/**
 * @fileoverview The What to plant tab (D67): "What can I plant now?", the gardener's own plants (any stage, the Seed
 * vault included) that can go in the ground now, in the server's groups (Last week to plant, Good to plant now, Coming up).
 *
 * E19 (a gardener with few or no plants; design.md v3, mock-ups a, d, e1 to e3): a search box at the top, labelled
 * "Find any plant to add, even if it's not time to plant it yet", whose results replace everything under it while
 * something is typed (CropSearch: the server's answer to what she typed, E24, hers first, then "Can go in now", "Not now
 * where you live", "No planting dates yet"); under her own groups "From your to-do list" (the crops her to-dos name, with "Not yet" and the next date when
 * closed), then, after a rule, "More you could plant now" with the See them card (to /what-to-plant/browse), then the
 * "Want something that's not here?" card, whose "Search ›" puts the cursor in the box. "+ Add" on any of those rows opens
 * the Add sheet (AddCropSheet, rule 7), and an added row turns to tick + Added, Open it › (useAddCrop).
 *
 * The backend decides all of it (services/plants/whatToPlant and whatToPlantBrowse): who may see the list, the groups
 * and their order, every sentence, the tag, the pills, the to-do matches, the count, and the notices for Free, a paused
 * plan and a place with no dates. This page draws what it is sent, with the note under the list (WhatToPlantMore). What
 * stays here: the layout, which group's extra rows are open, what is typed in the search, and the sheet. A tap on one of
 * her own rows opens that plant's page.
 *
 * The page may open on a search: ?q=blueberry fills the box (a link, a test); typing does not change the address.
 *
 * Where: the route /what-to-plant (AppRoutes), the fourth tab.
 * The list never waits for the whole catalogue (owner, 29 Oct, "e19 ok"): her own groups, the to-do rows and the search
 * card come with the list; "More you could plant now" comes from a second answer a moment later
 * (useWhatToPlantCatalogue), with a calm placeholder card in their spot until then (whatToPlant.catalogueLoading).
 *
 * API: GET /plants/what-to-plant (useWhatToPlant), GET /plants/what-to-plant/catalogue (useWhatToPlantCatalogue),
 * GET /plants/what-to-plant/search (CropSearch, E24),
 * POST /plants/what-to-plant/add (useAddCrop).
 * Words (en/es): whatToPlant.* (title, loading, showMore, seeGrowMore, search.label, catalogueLoading); billing.wall.resume; everything
 * else is the server's.
 * Watch out: a failure on our side says the standard sentence, reported to Sentry, with the support address and no
 * "try again" (ServerFailureNote).
 *
 * @module WhatToPlantPage
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { WhatToPlant } from '@/shared/api/whatToPlant';
import { WhatToPlantGroup } from './WhatToPlantGroup';
import { WhatToPlantMore } from './WhatToPlantMore';
import { useWhatToPlant } from './useWhatToPlant';
import { useWhatToPlantCatalogue, type WhatToPlantCatalogueReading } from './useWhatToPlantCatalogue';
import { useAddCrop, type AddCropState } from './useAddCrop';
import { CropRows } from './CropRows';
import { CropSearchBox, CropSearchResults } from './CropSearch';
import { AddCropSheet } from './AddCropSheet';

/** The classes of a card row's link words ("See them ›", "Search ›"). */
const CARD_LINK = 'flex shrink-0 items-center font-medium text-primary';

/**
 * The See them card's spot while the catalogue is on its way: a calm card the same shape, no spinner.
 *
 * @returns The placeholder.
 */
const CataloguePlaceholder: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  return (
    <div className="mt-7 border-t pt-4" aria-busy="true">
      <div className="flex min-h-14 items-center rounded-lg border border-dashed bg-card p-3">
        <p className="text-sm text-muted-foreground">{t('whatToPlant.catalogueLoading')}</p>
      </div>
    </div>
  );
};

/**
 * Everything under her own groups (E19): From your to-do list (with the list), then More you could plant now (from the
 * second answer, a moment later; its calm placeholder until then, the standard failure note if it fails), and the search
 * card.
 *
 * @param props.data - The list's answer.
 * @param props.catalogue - The second answer's reading.
 * @param props.addedWords - The server's "Added" and "Open it".
 * @param props.adder - The add's state.
 * @param props.onSearch - Puts the cursor in the search box.
 * @returns The sections.
 */
const Starters: React.FC<{
  data: WhatToPlant;
  catalogue: WhatToPlantCatalogueReading;
  addedWords: { word: string; open: string } | null;
  adder: AddCropState;
  onSearch: () => void;
}> = ({ data, catalogue, addedWords, adder, onSearch }) => {
  const seeThem = catalogue.data?.seeThem ?? null;
  return (
    <>
      {data.todos && addedWords && (
        <section className="mt-5" aria-label={data.todos.heading}>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{data.todos.heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{data.todos.sub}</p>
          <div className="mt-2">
            <CropRows rows={data.todos.rows} adder={adder} addedWords={addedWords} />
          </div>
        </section>
      )}
      {catalogue.error ? (
        <ServerFailureNote error={catalogue.error} where="whatToPlantCatalogue" className="mt-7 border-t pt-4" />
      ) : catalogue.isLoading ? (
        <CataloguePlaceholder />
      ) : seeThem && (
        <section className="mt-7 border-t pt-4" aria-label={seeThem.heading}>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{seeThem.heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{seeThem.sub}</p>
          <Link
            to="/what-to-plant/browse"
            className="mt-2 flex min-h-14 items-center gap-3 rounded-lg border bg-card p-3 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-base font-semibold text-foreground">{seeThem.title}</span>
              <span className="block text-sm text-muted-foreground">{seeThem.line}</span>
            </span>
            <span className={CARD_LINK}>
              {seeThem.link}
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        </section>
      )}
      {data.findAny && (
        <button
          type="button"
          onClick={onSearch}
          className="mt-4 flex min-h-14 w-full items-center gap-3 rounded-lg border bg-card p-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-base font-semibold text-foreground">{data.findAny.title}</span>
            <span className="block text-sm text-muted-foreground">{data.findAny.line}</span>
          </span>
          <span className={CARD_LINK}>
            {data.findAny.link}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </span>
        </button>
      )}
    </>
  );
};

/**
 * The tab: heading, the server's sub line, the search, the groups or the notice, the starters, the loading and failure
 * states.
 *
 * @returns The page.
 */
export const WhatToPlantPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { data, isLoading, error } = useWhatToPlant();
  const adder = useAddCrop();
  const [params] = useSearchParams();
  // A link may open the page on a search (?q=blueberry); typing changes only what is drawn, not the address.
  const [query, setQuery] = React.useState(() => params.get('q') ?? '');
  const searchBox = React.useRef<HTMLInputElement>(null);
  const open = data?.access === 'open';
  // The second answer: the See them card and the Add sheet's words, a moment after the list.
  const catalogue = useWhatToPlantCatalogue(open);
  const picker = catalogue.data?.picker ?? null;
  // The Add sheet's words: the list's own (a to-do row's Add works before the catalogue is in), else the catalogue's.
  const sheetWords = data?.addWords?.sheet ?? picker?.sheet ?? null;
  const addedWords = data?.addWords?.added ?? picker?.added ?? null;

  /** "Search ›": the cursor in the box, the box in sight. */
  const toSearch = React.useCallback(() => {
    searchBox.current?.focus();
    searchBox.current?.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-grow text-2xl font-bold text-foreground">{t('whatToPlant.title')}</h1>
      {data && <p className="mt-0.5 text-sm text-muted-foreground">{data.sub}</p>}

      {error ? (
        <ServerFailureNote error={error} where="whatToPlant" className="mt-4" />
      ) : isLoading || !data ? (
        <div className="mt-4" aria-busy="true">
          <p className="text-sm text-muted-foreground">{t('whatToPlant.loading')}</p>
          <div className="mt-3 space-y-2">
            {[0, 1, 2, 3, 4].map((n) => <div key={n} className="h-14 animate-pulse rounded-lg bg-muted" />)}
          </div>
        </div>
      ) : (
        <>
          {open && <CropSearchBox query={query} onChange={setQuery} label={t('whatToPlant.search.label')} inputRef={searchBox} />}
          {open && query.trim() ? (
            // The search is its own answer (E24); it needs only the Added words, which come with the list.
            addedWords ? <CropSearchResults query={query} adder={adder} addedWords={addedWords} />
              : <p className="mt-5 text-sm text-muted-foreground" aria-busy="true">{t('whatToPlant.catalogueLoading')}</p>
          ) : (
            <>
              {data.groups.map((group) => <WhatToPlantGroup key={group.key} group={group} />)}
              {open && <Starters data={data} catalogue={catalogue} addedWords={addedWords} adder={adder} onSearch={toSearch} />}
              {data.more && <WhatToPlantMore more={data.more} />}
              {data.notice && (
                <div className="mt-4 rounded-lg border bg-card p-4">
                  <p className="text-sm text-foreground">{data.notice.line}</p>
                  {data.notice.action && (
                    <Button asChild className="mt-3">
                      <Link to="/subscribe">
                        {data.notice.action === 'resume' ? t('billing.wall.resume') : t('whatToPlant.seeGrowMore')}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
          {sheetWords && <AddCropSheet adder={adder} words={sheetWords} />}
        </>
      )}
    </div>
  );
};
